import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {LoaderService} from "./loader.service";
import {EnvironmentService} from "./environment.service";
import {
    ActionResult,
    ActionResultList,
    GroupScoringConfiguration,
    IndividualScoringConfiguration, ScoringCategorization,
    ScoringDefinition
} from "@model";
import {finalize, Observable} from "rxjs";
import {map} from "rxjs/operators";


@Injectable({ providedIn:'root' })
export class LeagueScoringService {

    static readonly ITEM_IN_USE: number = 19;

    constructor(
        private http: HttpClient,
        private loaderService: LoaderService,
        private environmentService: EnvironmentService,
    ) {}

    updateScoringDefinition(leagueId: number, scoringDefinition: ScoringDefinition,
        onSuccess?: (scoringDefinition: ScoringDefinition) => void,
        onError?: (errorCode?: number) => void
    ): void {
        this.loaderService.startLoading();
        const url = scoringDefinition.id ? `/api/league/scoring/definition/update`: `/api/league/scoring/definition/add/${leagueId}`;
        this.http
            .post<ActionResult<ScoringDefinition>>(this.environmentService.getApiUrl() + url, scoringDefinition)
            .pipe(finalize(() => this.loaderService.dismissLoading()))
            .subscribe(actionResult => {
                actionResult?.result ? onSuccess?.(actionResult.result) : onError?.(actionResult?.resultCode);
            });
    }

    deleteScoringDefinition(scoringDefinition: ScoringDefinition,
        onSuccess?: () => void,
        onError?: (errorCode?: number) => void
    ): void {
        this.loaderService.startLoading();
        this.http
            .delete<ActionResult<boolean>>(`${this.environmentService.getApiUrl()}/api/league/scoring/definition/delete/${scoringDefinition.id}`)
            .pipe(finalize(() => this.loaderService.dismissLoading()))
            .subscribe(actionResult => {
                actionResult?.result ? onSuccess?.() : onError?.(actionResult?.resultCode);
            });
    }

    getScoringDefinitionsByLeagueId(leagueId: number): Observable<ScoringDefinition[]> {
        return this.http.get<ActionResultList<ScoringDefinition[], number>>(`${this.environmentService.getApiUrl()}/api/league/scoring/definition/getAll/${leagueId}`)
            .pipe(map(res => res?.result ? res.result : []));
    }

    updateIndividualScoringConfiguration(leagueId: number, configuration: IndividualScoringConfiguration,
                            onSuccess?: (configuration: IndividualScoringConfiguration) => void,
                            onError?: (errorCode?: number) => void
    ): void {
        this.loaderService.startLoading();
        const url = configuration.id ? `/api/league/scoring/individual/update`: `/api/league/scoring/individual/add/${leagueId}`;
        this.http
            .post<ActionResult<IndividualScoringConfiguration>>(this.environmentService.getApiUrl() + url, configuration)
            .pipe(finalize(() => this.loaderService.dismissLoading()))
            .subscribe(actionResult => {
                actionResult?.result ? onSuccess?.(actionResult.result) : onError?.(actionResult?.resultCode);
            });
    }

    deleteIndividualScoringConfiguration(configuration: IndividualScoringConfiguration,
                            onSuccess?: () => void,
                            onError?: (errorCode?: number) => void
    ): void {
        this.loaderService.startLoading();
        this.http
            .delete<ActionResult<boolean>>(`${this.environmentService.getApiUrl()}/api/league/scoring/individual/delete/${configuration.id}`)
            .pipe(finalize(() => this.loaderService.dismissLoading()))
            .subscribe(actionResult => {
                actionResult?.result ? onSuccess?.() : onError?.(actionResult?.resultCode);
            });
    }


    getIndividualScoringConfigurationsByLeagueId(leagueId: number): Observable<IndividualScoringConfiguration[]> {
        return this.http.get<ActionResultList<IndividualScoringConfiguration[], number>>(`${this.environmentService.getApiUrl()}/api/league/scoring/individual/getAll/${leagueId}`)
            .pipe(map(res => res?.result ? res.result : []));
    }


    updateGroupScoringConfiguration(leagueId: number, configuration: GroupScoringConfiguration,
                            onSuccess?: (configuration: GroupScoringConfiguration) => void,
                            onError?: (errorCode?: number) => void
    ): void {
        this.loaderService.startLoading();
        const url = configuration.id ? `/api/league/scoring/group/update`: `/api/league/scoring/group/add/${leagueId}`;
        this.http
            .post<ActionResult<GroupScoringConfiguration>>(this.environmentService.getApiUrl() + url, configuration)
            .pipe(finalize(() => this.loaderService.dismissLoading()))
            .subscribe(actionResult => {
                actionResult?.result ? onSuccess?.(actionResult.result) : onError?.(actionResult?.resultCode);
            });
    }


    deleteGroupScoringConfiguration(configuration: GroupScoringConfiguration,
                                         onSuccess?: () => void,
                                         onError?: (errorCode?: number) => void
    ): void {
        this.loaderService.startLoading();
        this.http
            .delete<ActionResult<boolean>>(`${this.environmentService.getApiUrl()}/api/league/scoring/group/delete/${configuration.id}`)
            .pipe(finalize(() => this.loaderService.dismissLoading()))
            .subscribe(actionResult => {
                actionResult?.result ? onSuccess?.() : onError?.(actionResult?.resultCode);
            });
    }


    getGroupScoringConfigurationsByLeagueId(leagueId: number): Observable<GroupScoringConfiguration[]> {
        return this.http.get<ActionResultList<GroupScoringConfiguration[], number>>(`${this.environmentService.getApiUrl()}/api/league/scoring/group/getAll/${leagueId}`)
            .pipe(map(res => res?.result ? res.result : []));
    }


    updateScoringCategorization(leagueId: number, scoringCategorization: ScoringCategorization,
                            onSuccess?: (scoringCategorization: ScoringCategorization) => void,
                            onError?: (errorCode?: number) => void
    ): void {
        this.loaderService.startLoading();
        const url = scoringCategorization.id ? `/api/league/scoring/categorization/update`: `/api/league/scoring/categorization/add/${leagueId}`;
        this.http
            .post<ActionResult<ScoringCategorization>>(this.environmentService.getApiUrl() + url, scoringCategorization)
            .pipe(finalize(() => this.loaderService.dismissLoading()))
            .subscribe(actionResult => {
                actionResult?.result ? onSuccess?.(actionResult.result) : onError?.(actionResult?.resultCode);
            });
    }


    getScoringCategorizationsByLeagueId(leagueId: number): Observable<ScoringCategorization[]> {
        return this.http.get<ActionResultList<ScoringCategorization[], number>>(`${this.environmentService.getApiUrl()}/api/league/scoring/categorization/getAll/${leagueId}`)
            .pipe(map(res => res?.result ? res.result : []));
    }


    deleteScoringCategorization(scoringCategorization: ScoringCategorization,
                            onSuccess?: () => void,
                            onError?: (errorCode?: number) => void
    ): void {
        this.loaderService.startLoading();
        this.http
            .delete<ActionResult<boolean>>(`${this.environmentService.getApiUrl()}/api/league/scoring/categorization/delete/${scoringCategorization.id}`)
            .pipe(finalize(() => this.loaderService.dismissLoading()))
            .subscribe(actionResult => {
                actionResult?.result ? onSuccess?.() : onError?.(actionResult?.resultCode);
            });
    }
}